/**
 * LocationSearch
 * Shared location autocomplete component
 * Works across all platforms - adapts to each platform's location API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X, Loader2, Plus, Check } from 'lucide-react';
import { debounce } from 'lodash';

export default function LocationSearch({
    platform,
    brandId,
    userId,
    selectedLocations = [],
    excludedLocations = [],
    onSelect,
    onRemove,
    onExclude,
    onRemoveExclusion,
    placeholder = 'Search locations...',
    maxSelections = 100,
    showExclusions = true,
    disabled = false
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState('include'); // 'include' or 'exclude'
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Debounced search function
    const searchLocations = useCallback(
        debounce(async (searchQuery) => {
            if (!searchQuery || searchQuery.length < 2) {
                setResults([]);
                return;
            }

            // Skip API call if required params are missing
            if (!brandId || !userId) {
                setError('Brand or user context not available');
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`/api/targeting/${platform}/locations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        brandId,
                        userId,
                        query: searchQuery
                    })
                });

                if (!response.ok) {
                    throw new Error('Search failed');
                }

                const data = await response.json();
                setResults(data.results || []);
            } catch (err) {
                console.error('Location search error:', err);
                setError('Failed to search locations. Please try again.');
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300),
        [platform, brandId, userId]
    );

    useEffect(() => {
        searchLocations(query);
    }, [query, searchLocations]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !inputRef.current?.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item) => {
        const totalSelected = selectedLocations.length + excludedLocations.length;
        if (totalSelected >= maxSelections) {
            return;
        }

        // Check if already in any list
        if (isSelected(item) || isExcluded(item)) {
            return;
        }

        if (mode === 'exclude' && onExclude) {
            onExclude(item);
        } else {
            onSelect(item);
        }

        setQuery('');
        setResults([]);
        setIsOpen(false);
    };

    const isSelected = (item) => {
        return selectedLocations.some(s =>
            (s.id && item.id && s.id === item.id) ||
            (s.key && item.key && s.key === item.key) ||
            (s.code && item.code && s.code === item.code)
        );
    };

    const isExcluded = (item) => {
        return excludedLocations.some(s =>
            (s.id && item.id && s.id === item.id) ||
            (s.key && item.key && s.key === item.key) ||
            (s.code && item.code && s.code === item.code)
        );
    };

    const getLocationType = (type) => {
        const types = {
            'country': 'Country',
            'region': 'Region/State',
            'state': 'State',
            'city': 'City',
            'zip': 'ZIP Code',
            'geo_market': 'DMA/Metro',
            'dma': 'DMA/Metro'
        };
        return types[type?.toLowerCase()] || type;
    };

    return (
        <div className="location-search">
            {/* Mode toggle for include/exclude */}
            {showExclusions && (
                <div className="mode-toggle">
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'include' ? 'active' : ''}`}
                        onClick={() => setMode('include')}
                    >
                        Include
                    </button>
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'exclude' ? 'active' : ''}`}
                        onClick={() => setMode('exclude')}
                    >
                        Exclude
                    </button>
                </div>
            )}

            {/* Selected locations */}
            {selectedLocations.length > 0 && (
                <div className="location-section">
                    <label className="section-label">Included Locations</label>
                    <div className="location-tags">
                        {selectedLocations.map(loc => (
                            <span key={loc.id || loc.key} className="location-tag included">
                                <MapPin size={12} />
                                <span className="tag-name">{loc.name}</span>
                                <span className="tag-type">{getLocationType(loc.type)}</span>
                                <button
                                    type="button"
                                    className="tag-remove"
                                    onClick={() => onRemove(loc)}
                                    disabled={disabled}
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Excluded locations */}
            {showExclusions && excludedLocations.length > 0 && (
                <div className="location-section">
                    <label className="section-label">Excluded Locations</label>
                    <div className="location-tags">
                        {excludedLocations.map(loc => (
                            <span key={loc.id || loc.key} className="location-tag excluded">
                                <MapPin size={12} />
                                <span className="tag-name">{loc.name}</span>
                                <span className="tag-type">{getLocationType(loc.type)}</span>
                                <button
                                    type="button"
                                    className="tag-remove"
                                    onClick={() => onRemoveExclusion(loc)}
                                    disabled={disabled}
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Search input */}
            <div className="search-input-wrapper">
                <MapPin size={16} className="search-icon" />
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder={mode === 'exclude' ? 'Search locations to exclude...' : placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    disabled={disabled}
                />
                {loading && <Loader2 size={16} className="loading-icon" />}
            </div>

            {/* Dropdown results */}
            {isOpen && (query.length >= 2 || results.length > 0) && (
                <div ref={dropdownRef} className="search-dropdown">
                    {error && (
                        <div className="dropdown-error">{error}</div>
                    )}

                    {!error && results.length === 0 && query.length >= 2 && !loading && (
                        <div className="dropdown-empty">
                            No locations found for "{query}"
                        </div>
                    )}

                    {results.map(item => {
                        const alreadySelected = isSelected(item);
                        const alreadyExcluded = isExcluded(item);
                        const isDisabled = alreadySelected || alreadyExcluded;

                        return (
                            <button
                                key={item.id || item.key}
                                type="button"
                                className={`dropdown-item ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => handleSelect(item)}
                                disabled={isDisabled}
                            >
                                <div className="item-content">
                                    <MapPin size={14} className="item-icon" />
                                    <div className="item-text">
                                        <span className="item-name">{item.name}</span>
                                        <span className="item-type">{getLocationType(item.type)}</span>
                                        {item.countryName && (
                                            <span className="item-country">{item.countryName}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="item-action">
                                    {alreadySelected && <span className="badge included">Included</span>}
                                    {alreadyExcluded && <span className="badge excluded">Excluded</span>}
                                    {!isDisabled && (
                                        mode === 'exclude'
                                            ? <span className="badge exclude-action">Exclude</span>
                                            : <Plus size={14} />
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            <style>{`
                .location-search {
                    position: relative;
                }

                .mode-toggle {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 0.75rem;
                }

                .mode-btn {
                    padding: 0.375rem 0.75rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 4px;
                    font-size: 0.8125rem;
                    color: var(--color-text-tertiary);
                    cursor: pointer;
                    transition: all 0.15s;
                }

                .mode-btn.active {
                    background: var(--color-primary);
                    border-color: var(--color-primary);
                    color: var(--color-text-inverted);
                }

                .location-section {
                    margin-bottom: 0.75rem;
                }

                .section-label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.375rem;
                }

                .location-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .location-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.8125rem;
                }

                .location-tag.included {
                    background: var(--color-primary);
                    color: var(--color-text-inverted);
                }

                .location-tag.excluded {
                    background: var(--color-error);
                    color: white;
                }

                .tag-type {
                    opacity: 0.7;
                    font-size: 0.75rem;
                }

                .tag-remove {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.125rem;
                    background: transparent;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    opacity: 0.7;
                }

                .tag-remove:hover {
                    opacity: 1;
                }

                .search-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .search-icon {
                    position: absolute;
                    left: 0.75rem;
                    color: var(--color-text-tertiary);
                    pointer-events: none;
                }

                .search-input {
                    width: 100%;
                    padding: 0.625rem 0.75rem 0.625rem 2.25rem;
                    background: var(--color-bg-primary);
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                    font-size: 0.875rem;
                    color: var(--color-text-primary);
                }

                .search-input:focus {
                    outline: none;
                    border-color: var(--color-primary);
                }

                .loading-icon {
                    position: absolute;
                    right: 0.75rem;
                    color: var(--color-text-tertiary);
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .search-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    margin-top: 0.25rem;
                    max-height: 300px;
                    overflow-y: auto;
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
                    z-index: 10000;
                }

                .dropdown-error,
                .dropdown-empty {
                    padding: 0.75rem;
                    text-align: center;
                    color: var(--color-text-tertiary);
                    font-size: 0.875rem;
                }

                .dropdown-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    padding: 0.625rem 0.75rem;
                    background: transparent;
                    border: none;
                    border-bottom: 1px solid var(--color-border);
                    text-align: left;
                    cursor: pointer;
                    transition: background 0.15s;
                    color: var(--color-text-primary);
                }

                .dropdown-item:last-child {
                    border-bottom: none;
                }

                .dropdown-item:hover:not(:disabled) {
                    background: var(--color-bg-secondary);
                }

                .dropdown-item.disabled {
                    opacity: 0.5;
                    cursor: default;
                }

                .item-content {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .item-icon {
                    color: var(--color-text-tertiary);
                }

                .item-text {
                    display: flex;
                    flex-direction: column;
                }

                .item-name {
                    font-size: 0.875rem;
                    color: var(--color-text-primary);
                }

                .item-type,
                .item-country {
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                }

                .item-action {
                    display: flex;
                    align-items: center;
                    color: var(--color-text-tertiary);
                }

                .badge {
                    padding: 0.125rem 0.375rem;
                    border-radius: 3px;
                    font-size: 0.6875rem;
                    font-weight: 500;
                }

                .badge.included {
                    background: var(--color-primary);
                    color: var(--color-text-inverted);
                }

                .badge.excluded {
                    background: var(--color-error);
                    color: white;
                }

                .badge.exclude-action {
                    background: var(--color-error);
                    color: white;
                }
            `}</style>
        </div>
    );
}
